import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router-dom";
import PageLayout from "../PageLayout";
import clubStore from "../../storage/ClubStore";
import appStore from "../../storage/AppStore";
import "../../common/game.css";
import Btn from "../../ui/Btn";

const getCount = (item, countKey, listKey) => {
  if (typeof item?.[countKey] === "number") return item[countKey];
  if (Array.isArray(item?.[listKey])) return item[listKey].length;
  return 0;
};

const GameTeamsList = ({role}) => {
  const navigate = useNavigate();

  useEffect(() => {
    clubStore.getGameTeams().catch(console.error);
  }, []);

  const basePath = role === "MGR" ? "/manager" : "/trainer";
  const teams = clubStore.club.game_teams || [];

  const onOpenDetails = (team) => navigate(`${basePath}/games/game_team_details/${team.id}`);

  const TeamCard = ({team}) => {
    const membersCount = getCount(team, "members_count", "members");
    const gamesCount = getCount(team, "games_count", "games");
    const tournamentsCount = getCount(team, "tournaments_count", "tournaments");
    const description = team.description || team.data?.description || "";

    return (
      <div className="game-card flex flex-column h-full">
        <div className="game-card-clickable flex flex-column flex-grow-1" role="button" tabIndex={0}
          onClick={() => onOpenDetails(team)} onKeyDown={(e) => e.key === "Enter" && onOpenDetails(team)}>
          <div className="game-card-top align-items-start">
            <div className="game-meta flex-1 min-w-0">
              <div className="flex align-items-center gap-2 mb-2">
                <i className="pi pi-users text-primary text-xl flex-shrink-0" aria-hidden="true"/>
                <div className="font-bold text-lg line-height-3 min-w-0 overflow-wrap-anywhere">{team.name}</div>
              </div>
              {description && <div className="text-sm text-color-secondary line-height-3 overflow-wrap-anywhere">{description}</div>}
            </div>
            <span className="badge badge-upcoming flex-shrink-0" title={appStore.tr.players || "Players"}>
              <i className="pi pi-user" aria-hidden="true"/>{membersCount}
            </span>
          </div>

          <div className="surface-ground border-round-lg p-3 my-2">
            <div className="flex align-items-center justify-content-between gap-2 py-2 border-bottom-1 surface-border">
              <div className="flex align-items-center gap-2 min-w-0 text-color-secondary">
                <i className="pi pi-users flex-shrink-0" aria-hidden="true"/>
                <span className="min-w-0 overflow-wrap-anywhere">{appStore.tr.players || "Players"}</span>
              </div>
              <span className="font-bold text-lg flex-shrink-0">{membersCount}</span>
            </div>
            <div className="flex align-items-center justify-content-between gap-2 py-2 border-bottom-1 surface-border">
              <div className="flex align-items-center gap-2 min-w-0 text-color-secondary">
                <i className="pi pi-flag flex-shrink-0" aria-hidden="true"/>
                <span className="min-w-0 overflow-wrap-anywhere">{appStore.tr.games || "Games"}</span>
              </div>
              <span className="font-bold text-lg flex-shrink-0">{gamesCount}</span>
            </div>
            <div className="flex align-items-center justify-content-between gap-2 py-2">
              <div className="flex align-items-center gap-2 min-w-0 text-color-secondary">
                <i className="pi pi-trophy flex-shrink-0" aria-hidden="true"/>
                <span className="min-w-0 overflow-wrap-anywhere">{appStore.tr.tournaments || "Tournaments"}</span>
              </div>
              <span className="font-bold text-lg flex-shrink-0">{tournamentsCount}</span>
            </div>
          </div>

          <div className="game-card-bottom mt-auto pt-2">
            <div className="game-badges">
              {team.age_group && <span className="badge badge-upcoming">{team.age_group}</span>}
              {team.sport?.name && <span className="badge badge-tournament">{team.sport.name}</span>}
            </div>
            <i className="pi pi-chevron-right text-color-secondary flex-shrink-0" aria-hidden="true"/>
          </div>
        </div>

        {(role === "MGR" || role === "TRN") && (
          <div className="game-card-actions flex-wrap align-items-center gap-2 border-top-1 surface-border mt-2">
            <Btn icon="pi pi-pencil" label={appStore.tr.edit || "Edit"} severity="secondary"
              onClick={() => navigate(`${basePath}/games/game_team/${team.id}`)}/>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout header={
      <div className="flex justify-content-between align-items-center gap-3 w-full">
        <h3 className="m-0">{appStore.tr.game_teams}</h3>
        <Btn icon="pi pi-plus" label={appStore.tr.add || "Add"}
          onClick={() => navigate(`${basePath}/games/game_team/new`)}/>
      </div>
    }>
      {!teams.length ? (
        <div className="game-card p-4 text-center">
          <div className="text-600">{appStore.tr.not_found || "No game teams yet"}</div>
        </div>
      ) : (
        <div className="grid m-0">
          {teams.map((team) => (
            <div className="col-12 lg:col-6 p-2" key={team.id}>
              <TeamCard team={team}/>
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default observer(GameTeamsList);
